import Controller from '@ember/controller';
import { action } from '@ember/object';
import RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import JournalContactInfo from 'donor-elf-web/lib/models/journal-contact-info';
import { type JournalSegmentRouteModel } from 'donor-elf-web/routes/funds/fund/journals/journal/segments/segment';

export default class JournalSegmentController extends Controller {
  @service declare router: RouterService;

  declare model: JournalSegmentRouteModel;

  @tracked saving: boolean = false;
  @tracked error?: any;
  @tracked inSegmentFilter: string = '';
  @tracked outSegmentFilter: string = '';
  @tracked inSegment: JournalContactInfo[] = [];
  @tracked outSegment: JournalContactInfo[] = [];

  get filteredInSegment() {
    if (isPresent(this.inSegmentFilter)) {
      let lowerCaseFilter = this.inSegmentFilter.toLowerCase();
      return this.inSegment.filter((x) => {
        return x.contact.nameLowerCase.includes(lowerCaseFilter);
      });
    } else {
      return this.inSegment;
    }
  }

  get filteredOutSegment() {
    if (isPresent(this.outSegmentFilter)) {
      let lowerCaseFilter = this.outSegmentFilter.toLowerCase();
      return this.outSegment.filter((x) => {
        return x.contact.nameLowerCase.includes(lowerCaseFilter);
      });
    } else {
      return this.outSegment;
    }
  }

  @action
  templateDidInsert() {
    let segmentName = this.model.segmentName;
    this.inSegment = sortByProperties(
      this.model.journalContactInfos.filter((info) =>
        info.journalContact.segments.some((x) => x === segmentName),
      ),
      ['contact.name'],
    );
    this.outSegment = sortByProperties(
      this.model.journalContactInfos.filter(
        (info) => !info.journalContact.segments.some((x) => x === segmentName),
      ),
      ['contact.name'],
    );
  }

  @action
  templateWillDestroy() {
    this.saving = false;
    this.error = undefined;
  }

  @action
  handleInSegmentFilterInput(e: any) {
    this.inSegmentFilter = e.target.value;
    if (this.outSegmentFilter !== '') {
      this.outSegmentFilter = '';
    }
  }

  @action
  handleOutSegmentFilterInput(e: any) {
    this.outSegmentFilter = e.target.value;
    if (this.inSegmentFilter !== '') {
      this.inSegmentFilter = '';
    }
  }

  @action
  removeFromSegment(jcInfo: JournalContactInfo) {
    this.outSegment = [jcInfo, ...this.outSegment];
    this.inSegment = this.inSegment.filter(
      (x) => x.contactId !== jcInfo.contactId,
    );
  }

  @action
  addToSegment(jcInfo: JournalContactInfo) {
    this.inSegment = [jcInfo, ...this.inSegment];
    this.outSegment = this.outSegment.filter(
      (x) => x.contactId !== jcInfo.contactId,
    );
  }

  @action
  async save() {
    this.saving = true;
    for (let i = 0; i < this.inSegment.length; i++) {
      let jcInfo = this.inSegment[i];
      if (
        !jcInfo.journalContact.segments.some(
          (x) => x === this.model.segmentName,
        )
      ) {
        jcInfo.journalContact.segments.push(this.model.segmentName);
        try {
          await jcInfo.journalContact.save();
        } catch (error) {
          this.error = error;
          this.saving = false;
          return;
        }
      }
    }
    for (let i = 0; i < this.outSegment.length; i++) {
      let jcInfo = this.outSegment[i];
      if (
        jcInfo.journalContact.segments.some((x) => x === this.model.segmentName)
      ) {
        jcInfo.journalContact.segments = jcInfo.journalContact.segments.filter(
          (x) => x !== this.model.segmentName,
        );
        try {
          await jcInfo.journalContact.save();
        } catch (error) {
          this.error = error;
          this.saving = false;
          return;
        }
      }
    }
    this.router.transitionTo('funds.fund.journals.journal.segments');
  }
}
